import { useEffect, useState } from 'react'
import type { JSX } from 'react'

import type { WorkQueueGroup, WorkQueueItem } from '@lensmira/domain'
import type { MessageKey } from '@lensmira/i18n'

import { useTranslation, type Translation } from '../i18n.js'
import { jobStatusWord, orderStatusWord, remakeStatusWord } from '../status-labels.js'
import { DataSurface, type SurfaceState } from './DataSurface.js'

/**
 * The work queue. `WRK-001`, `WRK-002`; `DESIGN-TOKENS.md` §16.3.
 *
 * > The work queue is the default landing surface for staff.
 *
 * What is waiting on somebody is the unit of work in a shop, and it spans jobs, orders and remakes
 * at once, so this is one screen rather than a filter on three others.
 *
 * **This screen decides nothing about what is waiting or how urgent it is.** Grouping, age, the
 * overdue flag and the action offered on each row all come from `packages/domain`'s `buildWorkQueue`,
 * through the endpoint, already decided. A screen that re-sorted or re-labelled would be a second
 * statement of the same rule, and the two would disagree the first time either changed.
 *
 * It is deliberately read-only for now. `WRK-002` gives each row an action, and an action here is a
 * write, which means the write queue and a permission check per action type. Offering a button that
 * does nothing is worse than offering none: it teaches the counter that half the buttons do not
 * work. So the action is shown as the label of what is needed, and the row says where to do it.
 */

export interface WorkQueueProps {
  readonly loadQueue: () => Promise<readonly WorkQueueGroup[]>
}

type LoadState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'error'; readonly message?: string }
  | { readonly kind: 'loaded'; readonly groups: readonly WorkQueueGroup[] }

/** Human wording for the entity a row stands for. The domain's `kind` is a data value, not copy. */
const KIND_LABEL: Readonly<Record<string, MessageKey>> = {
  job: 'workQueue.kind.job',
  order: 'workQueue.kind.order',
  remake: 'workQueue.kind.remake',
  repair: 'workQueue.kind.repair',
}

/**
 * Age in the shop's words.
 *
 * Days rather than hours because `WRK-002` is about what has been waiting, and a counter reads "3
 * days" faster than "71 hours". Today is "today" and not "0 days", which reads as an error.
 */
const ageLine = (t: Translation['t'], item: WorkQueueItem): string => {
  if (item.ageDays <= 0) return t('workQueue.waitingToday')
  return t('workQueue.waitingDays', { count: item.ageDays })
}

/** A kind the registry has no word for is shown as the data value rather than hidden. */
/**
 * The state the row is in, as a word.
 *
 * This chip printed `item.status` straight out of the wire, so a shopkeeper read `in_production`,
 * `cause_assigned` and `credit_expected`. Every other screen in the product goes through
 * `status-labels.ts` for exactly this; the queue was the one that did not, and it is the landing
 * surface.
 *
 * `ARC-020`: the value is unnarrowed by the time it reaches here, which is why each helper takes a
 * `string` and falls back to it. A repair has no status field at all (`WorkQueueItem` says so), so
 * there is no case for one.
 */
const statusWord = (t: Translation['t'], item: WorkQueueItem): string | null => {
  if (item.status === undefined) return null
  switch (item.kind) {
    case 'job':
      return jobStatusWord(t, item.status)
    case 'order':
      return orderStatusWord(t, item.status)
    case 'remake':
      return remakeStatusWord(t, item.status)
    case 'repair':
      return item.status
  }
}

const kindLabel = (t: Translation['t'], kind: string): string => {
  const key = KIND_LABEL[kind]
  return key === undefined ? kind : t(key)
}

export function WorkQueue({ loadQueue }: WorkQueueProps): JSX.Element {
  const { t } = useTranslation()
  const [state, setState] = useState<LoadState>({ kind: 'loading' })
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let cancelled = false
    setState({ kind: 'loading' })
    loadQueue()
      .then((groups) => {
        if (cancelled) return
        setState({ kind: 'loaded', groups })
      })
      .catch((error: unknown) => {
        if (cancelled) return
        setState({
          kind: 'error',
          ...(error instanceof Error ? { message: error.message } : {}),
        })
      })
    return () => {
      cancelled = true
    }
  }, [loadQueue, attempt])

  const total = state.kind === 'loaded' ? state.groups.reduce((sum, g) => sum + g.items.length, 0) : 0

  /**
   * `ST-02`'s six states, through one component. §16.3 makes this "the default landing surface for
   * staff", so it is the screen a shop looks at most and the one where a line of small grey text
   * standing in for a skeleton was most visible.
   *
   * The empty case is the shop's own and not `DataSurface`'s default, because an empty work queue
   * is the good outcome and the generic "none on file yet" reads as a failure here.
   */
  const surface: SurfaceState =
    state.kind === 'loading'
      ? { kind: 'loading' }
      : state.kind === 'error'
        ? {
            kind: 'error',
            message: state.message ?? t('workQueue.loadFailedNoDetail'),
            onRetry: () => {
              setAttempt((a) => a + 1)
            },
          }
        : total === 0
          ? { kind: 'empty' }
          : { kind: 'ready' }

  return (
    <section className="lm-screen" aria-labelledby="work-queue-heading">
      <h1 id="work-queue-heading" className="lm-screen__heading">
        {t('nav.queue')}
      </h1>

      <DataSurface
        state={surface}
        shape="rows"
        rows={5}
        what={t('workQueue.what')}
        emptyMessage={t('workQueue.empty')}
      >
        <>
          <p className="lm-hint" role="status">
            {t('workQueue.thingsWaiting', { count: total })}
          </p>

          {(state.kind === 'loaded' ? state.groups : []).map((group) => (
            <section key={group.group} className="lm-queue-group">
              <h2 className="lm-screen__subheading">
                {group.group}
                {' · '}
                <span className="lm-badge">{group.items.length}</span>
              </h2>

              <ul className="lm-queue-list">
                {group.items.map((item) => (
                  <li
                    key={`${item.kind}-${item.id}`}
                    className={
                      item.promiseState === 'overdue'
                        ? 'lm-queue-row lm-queue-row--overdue'
                        : 'lm-queue-row'
                    }
                  >
                    <span className="lm-queue-row__kind">{kindLabel(t, item.kind)}</span>
                    {/* `TAX-002a`: "**Job numbers must be short, speakable and unambiguous** - a
                        customer reads one out over the phone." This showed `item.id`, so §16.3's
                        landing surface read `Job 01a07d46-1cc5-7495-85ef-020e47d91b4d` while
                        `SAD-0001` sat unused on the same row: the queue carried no reference at all
                        and the screen had nothing else to print.

                        Still `dir="ltr"`, and the reason now matters more than it did: a job number
                        mirrored is a different job number, and this one gets read out loud.

                        Null for a kind with no series, which is a repair: the server used to send
                        the UUID and this printed it. A record with no reference now shows none, and
                        the name and the overdue mark beside it are what the row is for. */}
                    {item.reference === null ? null : (
                      <span className="lm-queue-row__id" dir="ltr">
                        {item.reference}
                      </span>
                    )}
                    {/* Whose work it is, which is what makes a row actionable. Five un-invoiced
                        orders read as five rows of `(not yet invoiced)` with nothing to tell them
                        apart, and an overdue repair showed its UUID, because `TAX-002a` gives
                        repairs no numbering series.

                        `dir="auto"` and never `ltr`: half the names in a Pakistani shop are Urdu
                        script, and this is the one field on the row that is a person's name rather
                        than a figure. */}
                    {item.customerName === undefined ? null : (
                      <span className="lm-queue-row__who" dir="auto">
                        {item.customerName}
                      </span>
                    )}
                    {statusWord(t, item) === null ? null : (
                      <span className="lm-chip lm-chip--job">{statusWord(t, item)}</span>
                    )}
                    <span className="lm-queue-row__age">{ageLine(t, item)}</span>
                    {item.promiseState === 'overdue' ? (
                      <span className="lm-queue-row__overdue-flag">
                        {t('workQueue.pastPromised')}
                      </span>
                    ) : null}
                    {item.promiseState === 'unset' ? (
                      // `JOB-004` computes a promised date from the lab's turnaround policy. A job
                      // without one cannot be reported late, so the gap is named rather than shown
                      // as though the job were on time.
                      <span className="lm-queue-row__overdue-flag">
                        {t('workQueue.noPromisedDate')}
                      </span>
                    ) : null}
                    <span className="lm-queue-row__action">{item.action.label}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </>
      </DataSurface>
    </section>
  )
}
